import { useEffect, useMemo, useRef, useState } from "react";
import { PlaybackEngine, type EngineState } from "../lib/audioEngine";
import type { DocumentRecord, Speed, Voice } from "../lib/db";
import { formatTime } from "../lib/format";
import { saveBlobViaNativeBridge } from "../lib/nativeBridge";

const VOICES: { id: Voice; label: string }[] = [
  { id: "arcas", label: "Arcas" },
  { id: "apollo", label: "Apollo" },
  { id: "orpheus", label: "Orpheus" },
  { id: "orion", label: "Orion" },
  { id: "luna", label: "Luna" },
  { id: "aurora", label: "Aurora" },
];

const SPEEDS: Speed[] = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.5];

interface Props {
  doc: DocumentRecord;
  chunks: string[];
  onBack: () => void;
  onPersist: (patch: Partial<DocumentRecord>) => void;
}

export function Reader({ doc, chunks, onBack, onPersist }: Props) {
  const engineRef = useRef<PlaybackEngine | null>(null);
  const [state, setState] = useState<EngineState | null>(null);
  const [voice, setVoice] = useState<Voice>(doc.voice);
  const [speed, setSpeed] = useState<Speed>(doc.speed);
  const [scrubValue, setScrubValue] = useState<number | null>(null);
  const [exportState, setExportState] = useState<{ done: number; total: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const engine = new PlaybackEngine(chunks, doc.voice, doc.speed, doc.chunkIndex);
    engineRef.current = engine;
    const unsubscribe = engine.subscribe(setState);
    return () => {
      unsubscribe();
      engine.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  useEffect(() => {
    if (!state) return;
    const timer = setInterval(() => {
      onPersist({
        chunkIndex: state.index,
        positionSec: state.currentTime,
        voice,
        speed,
        lastOpened: Date.now(),
      });
    }, 4000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.index, state?.currentTime, voice, speed]);

  const totalStr = useMemo(() => (state ? formatTime(state.totalEstimatedSec) : "0:00"), [state]);
  const elapsedStr = useMemo(() => (state ? formatTime(state.elapsedSec) : "0:00"), [state]);
  const remainingStr = useMemo(
    () => (state ? `-${formatTime(Math.max(0, state.totalEstimatedSec - state.elapsedSec))}` : "-0:00"),
    [state]
  );

  if (!state) {
    return (
      <div className="app">
        <p className="status-line">Preparing reader…</p>
      </div>
    );
  }

  const handlePlayPause = () => {
    if (state.isPlaying) engineRef.current?.pause();
    else void engineRef.current?.play();
  };

  const handleVoiceChange = (next: Voice) => {
    setVoice(next);
    void engineRef.current?.setVoice(next);
  };

  const handleSpeedChange = (next: Speed) => {
    setSpeed(next);
    engineRef.current?.setSpeed(next);
  };

  const handleExport = async () => {
    if (!engineRef.current || exportState) return;
    setExportError(null);
    setExportState({ done: 0, total: state.totalChunks });
    try {
      const blob = await engineRef.current.exportAsMp3((done, total) => setExportState({ done, total }));
      const filename = `${doc.title.replace(/[^\w\- ]+/g, "").trim().slice(0, 60) || "josh-mainframe"}.mp3`;

      const savedNatively = await saveBlobViaNativeBridge(blob, filename, "audio/mpeg");
      if (!savedNatively) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch {
      setExportError("Couldn't generate the full audio. Try again once the rest of the document plays back cleanly.");
    } finally {
      setExportState(null);
    }
  };

  const scrubberValue = scrubValue ?? state.elapsedSec;

  return (
    <div className="app">
      <div className="top-bar">
        <button className="icon-btn" onClick={onBack} aria-label="Back to library">
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="reader-title">{doc.title}</div>
          <div className="reader-sub">
            Chunk {state.index + 1} of {state.totalChunks}
          </div>
        </div>
        <button
          className={`icon-btn${showText ? " active" : ""}`}
          onClick={() => setShowText((v) => !v)}
          aria-label="Show text"
          title="Show text"
          aria-pressed={showText}
        >
          Aa
        </button>
        <button
          className="icon-btn"
          onClick={() => void handleExport()}
          disabled={!!exportState}
          aria-label="Export as MP3"
          title="Export as MP3"
        >
          {exportState ? "…" : "⭳"}
        </button>
      </div>

      {showText && (
        <div className="text-panel">
          <p className="reader-sub" style={{ marginBottom: 8 }}>
            Chunk {state.index + 1} of {state.totalChunks}
          </p>
          {chunks[state.index]}
        </div>
      )}

      <div className="reader-card">
        <div className="waveform" aria-hidden="true">
          {Array.from({ length: 24 }).map((_, i) => (
            <span
              key={i}
              className={state.isPlaying ? "bar playing" : "bar"}
              style={{ animationDelay: `${(i % 8) * 0.09}s` }}
            />
          ))}
        </div>

        <div className="select-row">
          <select
            className="chip-select"
            value={voice}
            onChange={(e) => handleVoiceChange(e.target.value as Voice)}
          >
            {VOICES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <input
            className="scrubber"
            type="range"
            min={0}
            max={Math.max(state.totalEstimatedSec, 1)}
            step={1}
            value={scrubberValue}
            onChange={(e) => setScrubValue(Number(e.target.value))}
            onMouseUp={(e) => {
              void engineRef.current?.seekToGlobal(Number((e.target as HTMLInputElement).value));
              setScrubValue(null);
            }}
            onTouchEnd={(e) => {
              void engineRef.current?.seekToGlobal(Number((e.target as HTMLInputElement).value));
              setScrubValue(null);
            }}
          />
          <div className="time-row">
            <span>{elapsedStr}</span>
            <span>{totalStr}</span>
          </div>
        </div>

        <div className="transport">
          <button onClick={() => void engineRef.current?.prev()} aria-label="Previous chunk">
            <span className="glyph">⏮</span>
            Prev
          </button>
          <button onClick={() => void engineRef.current?.seekRelative(-15)} aria-label="Rewind 15 seconds">
            <span className="glyph">↺15</span>
            15s
          </button>
          <button className="play-btn" onClick={handlePlayPause} aria-label={state.isPlaying ? "Pause" : "Play"}>
            {state.chunkStatus === "loading" ? "…" : state.isPlaying ? "❚❚" : "▶"}
          </button>
          <button onClick={() => void engineRef.current?.seekRelative(30)} aria-label="Forward 30 seconds">
            <span className="glyph">30↻</span>
            30s
          </button>
          <button onClick={() => void engineRef.current?.next()} aria-label="Next chunk">
            <span className="glyph">⏭</span>
            Next
          </button>
        </div>

        <div className="speed-row">
          {SPEEDS.map((s) => (
            <button
              key={s}
              className={`speed-chip${s === speed ? " active" : ""}`}
              onClick={() => handleSpeedChange(s)}
            >
              {s}x
            </button>
          ))}
        </div>

        {exportState && (
          <p className="status-line">
            Exporting MP3… chunk {exportState.done} of {exportState.total}
          </p>
        )}
        {exportError && <p className="status-line error">{exportError}</p>}
        {state.chunkStatus === "loading" && <p className="status-line">Generating audio…</p>}
        {state.chunkStatus === "error" && (
          <div>
            <p className="status-line error">{state.chunkError ?? "Couldn't generate audio for this part."}</p>
            <button className="btn btn-secondary btn-block" onClick={() => void engineRef.current?.retryCurrent()}>
              Retry
            </button>
          </div>
        )}
        {state.isFinished && <p className="status-line success">Finished reading. Nice work.</p>}
        <p className="reader-sub" style={{ textAlign: "center" }}>
          {remainingStr} remaining (estimated)
        </p>
      </div>
    </div>
  );
}
